/**
 * Pass-map derivation utilities.
 *
 * Turns a player's logged `Pass` events into arrows (start -> end, when the
 * operator recorded a destination) and start-point markers (when they did
 * not), plus the aggregate footer stats shown alongside the pitch.
 *
 * Uses the same StatsBomb coordinate space (x in [0,120], y in [0,80]) and
 * outcome classification (`Complete` = successful; anything else = not) as
 * `usePlayerStats.ts` and `heatMapZones.ts`, so a player's pass counts here
 * always match their stat badges elsewhere in the cockpit.
 */
import type { MatchEvent } from "../../../store/useMatchLogStore";

export interface PassArrow {
  id: string;
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  success: boolean;
}

export interface PassMarker {
  id: string;
  x: number;
  y: number;
  success: boolean;
}

export interface PassMapData {
  arrows: PassArrow[];
  markers: PassMarker[];
  total: number;
  successful: number;
  unsuccessful: number;
  /** Successful / total, rounded to a whole percent. 0 when total is 0. */
  accuracy: number;
}

const isSuccessfulPass = (outcome: unknown): boolean =>
  String(outcome ?? "").toLowerCase() === "complete";

/**
 * Compute pass-map arrows/markers/stats for a single player from a list of
 * match events.
 *
 * @param events   - MatchEvent[]
 * @param playerId - player whose Pass events to include
 */
export function computePassMapData(
  events: MatchEvent[],
  playerId: string,
): PassMapData {
  const arrows: PassArrow[] = [];
  const markers: PassMarker[] = [];
  let successful = 0;
  let unsuccessful = 0;

  events.forEach((ev, index) => {
    if (ev.type !== "Pass" || ev.player_id !== playerId) return;
    if (!ev.location || ev.location.length < 2) return;

    const [x1, y1] = ev.location;
    const success = isSuccessfulPass(ev.data?.outcome);
    if (success) successful++;
    else unsuccessful++;

    const id = ev._id ?? `${playerId}-${ev.timestamp ?? ""}-${index}`;
    const end = ev.data?.end_location as [number, number] | undefined;

    if (end && end.length >= 2) {
      arrows.push({ id, x1, y1, x2: end[0], y2: end[1], success });
    } else {
      markers.push({ id, x: x1, y: y1, success });
    }
  });

  const total = successful + unsuccessful;
  const accuracy = total > 0 ? Math.round((successful / total) * 100) : 0;

  return { arrows, markers, total, successful, unsuccessful, accuracy };
}
